export const demoArticles = [
  {
    id: '1',
    title: 'The Complete Guide to Mindful Eating',
    summary: 'Learn how to develop a healthier relationship with food through mindfulness practices that can transform your eating habits and overall well-being.',
    content: `
      <p>In our fast-paced world, eating has often become a mindless activity. We eat while working, watching TV, or scrolling through our phones, barely registering what we're consuming. Mindful eating offers a different approach—one that can revolutionize your relationship with food.</p>
      
      <h2>What is Mindful Eating?</h2>
      <p>Mindful eating is the practice of paying full attention to your food and eating experience. It involves being present in the moment, aware of your thoughts, feelings, and physical sensations while eating.</p>
      
      <div style="background: #fff3e0; border: 1px solid #ffc107; border-radius: 8px; padding: 16px; margin: 20px 0;">
        <div style="font-weight: bold; color: #f57c00; margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">💡 Key Insight</div>
        <p>Mindful eating isn't about restriction or judgment—it's about awareness and intention in your food choices.</p>
      </div>
      
      <h2>Benefits of Mindful Eating</h2>
      <ul>
        <li><strong>Better digestion:</strong> Eating slowly aids the digestive process</li>
        <li><strong>Weight management:</strong> Increased awareness of hunger and fullness cues</li>
        <li><strong>Reduced stress:</strong> Creates a calming, meditative eating experience</li>
        <li><strong>Enhanced satisfaction:</strong> Greater appreciation for food flavors and textures</li>
        <li><strong>Improved relationship with food:</strong> Less guilt and more enjoyment</li>
      </ul>
      
      <h3>Getting Started: Simple Steps</h3>
      <p>Beginning your mindful eating journey doesn't require drastic changes. Start with these simple practices:</p>
      
      <div style="background: #e3f2fd; border-left: 4px solid #1976d2; padding: 16px; margin: 20px 0; border-radius: 0 8px 8px 0; font-style: italic; color: #0d47a1;">
        "The act of eating is sacred. When we eat with awareness, we transform a basic necessity into a moment of connection with ourselves and our environment."
      </div>
      
      <p><strong>1. Eliminate distractions:</strong> Put away phones, turn off the TV, and focus solely on your meal.</p>
      <p><strong>2. Eat slowly:</strong> Take time to chew thoroughly and savor each bite. Aim to make your meals last at least 20 minutes.</p>
      <p><strong>3. Engage your senses:</strong> Notice the colors, textures, smells, and flavors of your food.</p>
      <p><strong>4. Check in with hunger:</strong> Before eating, assess your hunger level on a scale of 1-10. Stop eating when you reach about 7.</p>
      
      <h2>Common Challenges and Solutions</h2>
      <p>Like any new habit, mindful eating comes with challenges. Here are common obstacles and how to overcome them:</p>
      
      <p><strong>Time constraints:</strong> Even if you only have 10 minutes, you can still eat mindfully. Focus on being present rather than rushing.</p>
      <p><strong>Emotional eating:</strong> When emotions drive eating, pause and acknowledge what you're feeling before deciding whether you're truly hungry.</p>
      
      <div style="background: #fff3e0; border: 1px solid #ffc107; border-radius: 8px; padding: 16px; margin: 20px 0;">
        <div style="font-weight: bold; color: #f57c00; margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">⚡ Quick Tip</div>
        <p>Try the "5-4-3-2-1" technique: Notice 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, and 1 you can taste.</p>
      </div>
      
      <h2>Making It a Lifestyle</h2>
      <p>Mindful eating becomes most powerful when it's integrated into your daily routine. Start with one meal per day, gradually expanding your practice as it becomes more natural.</p>
      
      <p>Remember, mindful eating is a practice, not perfection. Be patient with yourself as you develop this new relationship with food.</p>
    `,
    author: {
      name: 'Dr. Sarah Rodriguez',
      title: 'Registered Dietitian & Wellness Coach',
      avatar: 'SR'
    },
    category: 'Nutrition',
    readTime: '8 min read',
    publishDate: 'March 15, 2024',
    tags: [ 'Mindful Eating', 'Nutrition', 'Wellness', 'Healthy Habits', 'Mental Health' ],
    relatedArticles: [
      {
        id: '2',
        title: '5 Foods That Boost Your Immune System',
        type: 'Article',
        readTime: '6 min read',
        image: '🍎'
      },
      {
        id: '3',
        title: 'Managing Stress Through Breathing',
        type: 'Article',
        readTime: '5 min read',
        image: '🧘'
      }
    ]
  },
  {
    id: '2',
    title: '5 Foods That Boost Your Immune System',
    summary: 'Discover the power foods that can strengthen your immune system and help you stay healthy year-round.',
    content: `
      <p>Your immune system is your body's defense mechanism against illness and disease. While there's no magic pill to prevent all illnesses, certain foods can give your immune system the boost it needs to function optimally.</p>
      
      <h2>1. Citrus Fruits</h2>
      <p>Citrus fruits like oranges, grapefruits, lemons, and limes are packed with vitamin C, which is essential for immune function. Vitamin C helps stimulate the production of white blood cells, which are key to fighting infections.</p>
      
      <h2>2. Red Bell Peppers</h2>
      <p>Surprisingly, red bell peppers contain twice as much vitamin C as citrus fruits. They're also rich in beta carotene, which your body converts to vitamin A—another important nutrient for immune health.</p>
      
      <h2>3. Broccoli</h2>
      <p>This superfood is loaded with vitamins A, C, and E, plus fiber and many other antioxidants. Broccoli is one of the healthiest vegetables you can put on your plate.</p>
      
      <h2>4. Garlic</h2>
      <p>Garlic has been used for centuries for its medicinal properties. It contains compounds like allicin that have been shown to boost immune function and may help reduce the severity of cold symptoms.</p>
      
      <h2>5. Yogurt</h2>
      <p>Look for yogurts that have "live and active cultures" printed on the label. These probiotics can help stimulate your immune system and may help your body fight diseases.</p>
      
      <h2>Additional Immune-Boosting Tips</h2>
      <ul>
        <li>Stay hydrated by drinking plenty of water</li>
        <li>Get adequate sleep (7-9 hours per night)</li>
        <li>Exercise regularly</li>
        <li>Manage stress through meditation or yoga</li>
        <li>Wash your hands frequently</li>
      </ul>
    `,
    author: {
      name: 'Dr. Michael Chen',
      title: 'Nutritionist & Health Coach',
      avatar: 'MC'
    },
    category: 'Nutrition',
    readTime: '6 min read',
    publishDate: 'March 12, 2024',
    tags: [ 'Immune System', 'Nutrition', 'Health', 'Vitamins', 'Prevention' ]
  },
  {
    id: '3',
    title: 'Better Sleep Habits for Busy Professionals',
    summary: 'Transform your sleep quality with these evidence-based strategies designed for busy professionals.',
    content: `
      <p>Quality sleep is not a luxury—it's a necessity for optimal performance, health, and well-being. Yet many busy professionals struggle to get the rest they need. Here's how to reclaim your sleep.</p>
      
      <h2>The Science of Sleep</h2>
      <p>During sleep, your body repairs tissues, consolidates memories, and regulates hormones. Adults need 7-9 hours of quality sleep per night for optimal function.</p>
      
      <h2>Create a Sleep Sanctuary</h2>
      <ul>
        <li><strong>Keep it cool:</strong> 65-68°F (18-20°C) is ideal</li>
        <li><strong>Make it dark:</strong> Use blackout curtains or an eye mask</li>
        <li><strong>Keep it quiet:</strong> Use earplugs or white noise if needed</li>
        <li><strong>Invest in comfort:</strong> A supportive mattress and pillows</li>
      </ul>
      
      <h2>Establish a Wind-Down Routine</h2>
      <p>Create a consistent 30-60 minute routine before bed:</p>
      <ol>
        <li>Put away electronic devices 1 hour before bed</li>
        <li>Take a warm bath or shower</li>
        <li>Read a book or practice gentle stretching</li>
        <li>Write in a gratitude journal</li>
        <li>Practice deep breathing or meditation</li>
      </ol>
      
      <h2>Optimize Your Day for Better Sleep</h2>
      <ul>
        <li><strong>Morning light exposure:</strong> Get 10-30 minutes of natural light within an hour of waking</li>
        <li><strong>Limit caffeine:</strong> Avoid caffeine after 2 PM</li>
        <li><strong>Exercise regularly:</strong> But not within 3 hours of bedtime</li>
        <li><strong>Manage stress:</strong> Practice stress-reduction techniques throughout the day</li>
      </ul>
    `,
    author: {
      name: 'Dr. Lisa Thompson',
      title: 'Sleep Medicine Specialist',
      avatar: 'LT'
    },
    category: 'Sleep',
    readTime: '5 min read',
    publishDate: 'March 10, 2024',
    tags: [ 'Sleep', 'Wellness', 'Productivity', 'Health', 'Routine' ]
  },
  {
    id: '4',
    title: 'Managing Stress Through Breathing',
    summary: 'Learn powerful breathing techniques that can help you manage stress and anxiety in just minutes.',
    content: `
      <p>Breathing is something we do automatically, but when done mindfully, it becomes a powerful tool for managing stress and promoting relaxation. These techniques can be practiced anywhere, anytime.</p>
      
      <h2>The 4-7-8 Breathing Technique</h2>
      <p>This technique is perfect for reducing anxiety and promoting sleep:</p>
      <ol>
        <li>Inhale through your nose for 4 counts</li>
        <li>Hold your breath for 7 counts</li>
        <li>Exhale through your mouth for 8 counts</li>
        <li>Repeat 3-4 times</li>
      </ol>
      
      <h2>Box Breathing</h2>
      <p>Also known as square breathing, this technique is used by Navy SEALs to stay calm under pressure:</p>
      <ol>
        <li>Inhale for 4 counts</li>
        <li>Hold for 4 counts</li>
        <li>Exhale for 4 counts</li>
        <li>Hold for 4 counts</li>
        <li>Repeat for 4-5 cycles</li>
      </ol>
      
      <h2>Diaphragmatic Breathing</h2>
      <p>This deep breathing technique engages your diaphragm and activates your body's relaxation response:</p>
      <ol>
        <li>Place one hand on your chest and one on your belly</li>
        <li>Breathe in slowly through your nose, letting your belly rise</li>
        <li>Your chest should move very little</li>
        <li>Exhale slowly through pursed lips</li>
        <li>Continue for 5-10 minutes</li>
      </ol>
      
      <h2>When to Use These Techniques</h2>
      <ul>
        <li>Before important meetings or presentations</li>
        <li>When feeling overwhelmed or anxious</li>
        <li>During breaks throughout the day</li>
        <li>Before bedtime to promote relaxation</li>
        <li>During meditation or mindfulness practice</li>
      </ul>
    `,
    author: {
      name: 'Dr. James Wilson',
      title: 'Clinical Psychologist & Mindfulness Expert',
      avatar: 'JW'
    },
    category: 'Mental Health',
    readTime: '5 min read',
    publishDate: 'March 8, 2024',
    tags: [ 'Stress Management', 'Breathing', 'Mental Health', 'Relaxation', 'Mindfulness' ]
  },
  {
    id: '5',
    title: 'Home Workout: No Equipment Needed',
    summary: 'Get fit at home with this comprehensive bodyweight workout that requires no equipment.',
    content: `
      <p>You don't need a gym membership or expensive equipment to get a great workout. This bodyweight routine targets all major muscle groups and can be done anywhere.</p>
      
      <h2>Warm-Up (5 minutes)</h2>
      <ul>
        <li>Arm circles: 30 seconds forward, 30 seconds backward</li>
        <li>Leg swings: 30 seconds each leg</li>
        <li>Jumping jacks: 1 minute</li>
        <li>High knees: 1 minute</li>
        <li>Butt kicks: 1 minute</li>
      </ul>
      
      <h2>Main Workout (20-30 minutes)</h2>
      <p>Perform each exercise for 45 seconds, rest for 15 seconds, then move to the next exercise. Complete 2-3 rounds.</p>
      
      <h3>Upper Body</h3>
      <ul>
        <li><strong>Push-ups:</strong> Keep your body straight and lower until your chest nearly touches the floor</li>
        <li><strong>Pike push-ups:</strong> Start in downward dog position and perform push-ups</li>
        <li><strong>Tricep dips:</strong> Use a chair or step, lower your body and push back up</li>
      </ul>
      
      <h3>Lower Body</h3>
      <ul>
        <li><strong>Squats:</strong> Lower until your thighs are parallel to the floor</li>
        <li><strong>Lunges:</strong> Step forward and lower your back knee toward the ground</li>
        <li><strong>Single-leg glute bridges:</strong> Lift one leg and raise your hips</li>
      </ul>
      
      <h3>Core</h3>
      <ul>
        <li><strong>Plank:</strong> Hold a straight line from head to heels</li>
        <li><strong>Mountain climbers:</strong> Alternate bringing knees to chest</li>
        <li><strong>Bicycle crunches:</strong> Bring opposite elbow to knee</li>
      </ul>
      
      <h2>Cool-Down (5 minutes)</h2>
      <ul>
        <li>Cat-cow stretches</li>
        <li>Child's pose</li>
        <li>Forward fold</li>
        <li>Quad stretches</li>
        <li>Deep breathing</li>
      </ul>
    `,
    author: {
      name: 'Sarah Martinez',
      title: 'Certified Personal Trainer',
      avatar: 'SM'
    },
    category: 'Exercise',
    readTime: '7 min read',
    publishDate: 'March 5, 2024',
    tags: [ 'Exercise', 'Home Workout', 'Bodyweight', 'Fitness', 'No Equipment' ]
  },
  {
    id: '6',
    title: 'The Science of Hydration',
    summary: 'Understand how proper hydration affects your body and learn the best strategies for staying hydrated.',
    content: `
      <p>Water makes up about 60% of your body weight and is essential for every bodily function. Yet many people don't drink enough water throughout the day.</p>
      
      <h2>Why Hydration Matters</h2>
      <p>Water is crucial for:</p>
      <ul>
        <li>Regulating body temperature</li>
        <li>Lubricating joints</li>
        <li>Protecting sensitive tissues</li>
        <li>Removing waste through urination and perspiration</li>
        <li>Maintaining blood pressure</li>
        <li>Supporting brain function</li>
      </ul>
      
      <h2>How Much Water Do You Need?</h2>
      <p>The "8 glasses a day" rule is a good starting point, but individual needs vary based on:</p>
      <ul>
        <li>Activity level</li>
        <li>Climate</li>
        <li>Overall health</li>
        <li>Body size</li>
        <li>Pregnancy or breastfeeding status</li>
      </ul>
      
      <h2>Signs of Dehydration</h2>
      <ul>
        <li>Thirst</li>
        <li>Dark yellow urine</li>
        <li>Fatigue</li>
        <li>Dizziness</li>
        <li>Confusion</li>
        <li>Dry mouth and lips</li>
      </ul>
      
      <h2>Tips for Better Hydration</h2>
      <ul>
        <li>Start your day with a glass of water</li>
        <li>Keep a water bottle with you</li>
        <li>Eat water-rich foods like fruits and vegetables</li>
        <li>Drink before you feel thirsty</li>
        <li>Monitor your urine color</li>
        <li>Consider your activity level and environment</li>
      </ul>
    `,
    author: {
      name: 'Dr. Emily Johnson',
      title: 'Sports Medicine Physician',
      avatar: 'EJ'
    },
    category: 'Nutrition',
    readTime: '4 min read',
    publishDate: 'March 3, 2024',
    tags: [ 'Hydration', 'Health', 'Nutrition', 'Wellness', 'Water' ]
  },
  {
    id: '7',
    title: 'Building Mental Resilience',
    summary: 'Develop the mental strength to bounce back from setbacks and thrive in challenging situations.',
    content: `
      <p>Mental resilience is the ability to adapt and bounce back from adversity, trauma, or significant sources of stress. It's not about avoiding difficult situations, but about navigating through them effectively.</p>
      
      <h2>What is Mental Resilience?</h2>
      <p>Resilience involves behaviors, thoughts, and actions that can be learned and developed by anyone. It's not a trait that people either have or don't have.</p>
      
      <h2>Key Components of Resilience</h2>
      <ul>
        <li><strong>Self-awareness:</strong> Understanding your emotions and reactions</li>
        <li><strong>Self-regulation:</strong> Managing your emotions and behaviors</li>
        <li><strong>Optimism:</strong> Maintaining a positive outlook</li>
        <li><strong>Mental agility:</strong> Thinking flexibly and creatively</li>
        <li><strong>Connection:</strong> Building strong relationships</li>
      </ul>
      
      <h2>Strategies to Build Resilience</h2>
      
      <h3>1. Practice Self-Care</h3>
      <ul>
        <li>Get adequate sleep</li>
        <li>Eat a balanced diet</li>
        <li>Exercise regularly</li>
        <li>Engage in activities you enjoy</li>
      </ul>
      
      <h3>2. Develop Problem-Solving Skills</h3>
      <ul>
        <li>Break problems into smaller parts</li>
        <li>Consider multiple solutions</li>
        <li>Learn from past experiences</li>
        <li>Ask for help when needed</li>
      </ul>
      
      <h3>3. Build Strong Relationships</h3>
      <ul>
        <li>Maintain close friendships</li>
        <li>Seek support when needed</li>
        <li>Help others when you can</li>
        <li>Join groups with similar interests</li>
      </ul>
      
      <h3>4. Practice Mindfulness</h3>
      <ul>
        <li>Meditation</li>
        <li>Deep breathing exercises</li>
        <li>Mindful walking</li>
        <li>Gratitude journaling</li>
      </ul>
    `,
    author: {
      name: 'Dr. Rachel Green',
      title: 'Clinical Psychologist',
      avatar: 'RG'
    },
    category: 'Mental Health',
    readTime: '6 min read',
    publishDate: 'March 1, 2024',
    tags: [ 'Mental Health', 'Resilience', 'Wellness', 'Psychology', 'Self-Care' ]
  },
  {
    id: '8',
    title: 'The Benefits of Walking',
    summary: 'Discover why walking is one of the best exercises you can do for your physical and mental health.',
    content: `
      <p>Walking is often overlooked as a form of exercise, but it's one of the most accessible and beneficial activities you can do. It requires no special equipment, can be done anywhere, and offers numerous health benefits.</p>
      
      <h2>Physical Health Benefits</h2>
      <ul>
        <li><strong>Cardiovascular health:</strong> Strengthens your heart and improves circulation</li>
        <li><strong>Weight management:</strong> Burns calories and helps maintain a healthy weight</li>
        <li><strong>Bone strength:</strong> Helps prevent osteoporosis and bone loss</li>
        <li><strong>Muscle tone:</strong> Strengthens legs, glutes, and core muscles</li>
        <li><strong>Balance and coordination:</strong> Reduces risk of falls</li>
        <li><strong>Immune function:</strong> Boosts your immune system</li>
      </ul>
      
      <h2>Mental Health Benefits</h2>
      <ul>
        <li><strong>Stress reduction:</strong> Releases endorphins that improve mood</li>
        <li><strong>Better sleep:</strong> Helps regulate sleep patterns</li>
        <li><strong>Improved focus:</strong> Enhances cognitive function</li>
        <li><strong>Creativity boost:</strong> Walking can spark new ideas</li>
        <li><strong>Social connection:</strong> Walking with others builds relationships</li>
      </ul>
      
      <h2>How to Get Started</h2>
      <ul>
        <li>Start with 10-15 minutes per day</li>
        <li>Gradually increase duration and intensity</li>
        <li>Find a comfortable pace</li>
        <li>Choose safe, well-lit routes</li>
        <li>Wear comfortable, supportive shoes</li>
        <li>Stay hydrated</li>
      </ul>
      
      <h2>Walking Tips for Maximum Benefits</h2>
      <ul>
        <li>Maintain good posture</li>
        <li>Swing your arms naturally</li>
        <li>Take deep breaths</li>
        <li>Vary your routes</li>
        <li>Walk with a friend or pet</li>
        <li>Listen to music or podcasts</li>
      </ul>
    `,
    author: {
      name: 'Dr. Mark Davis',
      title: 'Exercise Physiologist',
      avatar: 'MD'
    },
    category: 'Exercise',
    readTime: '5 min read',
    publishDate: 'February 28, 2024',
    tags: [ 'Walking', 'Exercise', 'Health', 'Fitness', 'Wellness' ]
  },
  {
    id: '9',
    title: 'Understanding Your Body\'s Circadian Rhythm',
    summary: 'Learn how your body\'s internal clock affects your health and how to optimize it.',
    content: `
      <p>Your circadian rhythm is your body's internal 24-hour clock that regulates sleep-wake cycles, hormone production, and other physiological processes. Understanding and working with this rhythm can significantly improve your health and well-being.</p>
      
      <h2>What is Circadian Rhythm?</h2>
      <p>Circadian rhythms are physical, mental, and behavioral changes that follow a 24-hour cycle. They respond primarily to light and darkness in your environment.</p>
      
      <h2>How Circadian Rhythm Works</h2>
      <p>Your brain's suprachiasmatic nucleus (SCN) acts as the master clock, receiving light signals from your eyes and synchronizing all your body's clocks.</p>
      
      <h2>Key Hormones and Their Timing</h2>
      <ul>
        <li><strong>Cortisol:</strong> Peaks in the morning, helps you wake up</li>
        <li><strong>Melatonin:</strong> Increases in the evening, promotes sleep</li>
        <li><strong>Growth hormone:</strong> Released during deep sleep</li>
        <li><strong>Insulin:</strong> More sensitive in the morning</li>
      </ul>
      
      <h2>Signs of Circadian Disruption</h2>
      <ul>
        <li>Difficulty falling asleep or staying asleep</li>
        <li>Feeling tired during the day</li>
        <li>Mood changes</li>
        <li>Digestive issues</li>
        <li>Weakened immune function</li>
        <li>Difficulty concentrating</li>
      </ul>
      
      <h2>How to Optimize Your Circadian Rhythm</h2>
      
      <h3>Morning Routine</h3>
      <ul>
        <li>Get bright light exposure within 1 hour of waking</li>
        <li>Eat breakfast at a consistent time</li>
        <li>Exercise in the morning if possible</li>
        <li>Avoid caffeine for the first 90 minutes after waking</li>
      </ul>
      
      <h3>Evening Routine</h3>
      <ul>
        <li>Dim lights 2-3 hours before bed</li>
        <li>Avoid screens 1 hour before sleep</li>
        <li>Keep your bedroom cool and dark</li>
        <li>Go to bed and wake up at the same time daily</li>
      </ul>
    `,
    author: {
      name: 'Dr. Amanda Lee',
      title: 'Sleep Medicine Specialist',
      avatar: 'AL'
    },
    category: 'Sleep',
    readTime: '6 min read',
    publishDate: 'February 25, 2024',
    tags: [ 'Circadian Rhythm', 'Sleep', 'Health', 'Biology', 'Wellness' ]
  },
  {
    id: '10',
    title: 'The Power of Gratitude',
    summary: 'Discover how practicing gratitude can transform your mental health and overall well-being.',
    content: `
      <p>Gratitude is more than just saying "thank you." It's a powerful practice that can rewire your brain, improve your relationships, and enhance your overall quality of life.</p>
      
      <h2>What is Gratitude?</h2>
      <p>Gratitude is the appreciation of what is valuable and meaningful to you. It's recognizing the good in your life, both big and small.</p>
      
      <h2>The Science of Gratitude</h2>
      <p>Research shows that gratitude can:</p>
      <ul>
        <li>Increase happiness and life satisfaction</li>
        <li>Reduce depression and anxiety</li>
        <li>Improve sleep quality</li>
        <li>Strengthen relationships</li>
        <li>Boost immune function</li>
        <li>Reduce stress and inflammation</li>
      </ul>
      
      <h2>How Gratitude Changes Your Brain</h2>
      <p>Regular gratitude practice can:</p>
      <ul>
        <li>Increase activity in the prefrontal cortex</li>
        <li>Activate the brain's reward system</li>
        <li>Strengthen neural pathways associated with positive emotions</li>
        <li>Reduce activity in the amygdala (fear center)</li>
      </ul>
      
      <h2>Simple Gratitude Practices</h2>
      
      <h3>1. Gratitude Journaling</h3>
      <p>Write down 3-5 things you're grateful for each day. Be specific and focus on the details.</p>
      
      <h3>2. Gratitude Letters</h3>
      <p>Write a letter to someone who has made a positive impact on your life. You can send it or keep it for yourself.</p>
      
      <h3>3. Gratitude Meditation</h3>
      <p>Spend 5-10 minutes focusing on things you're grateful for. Visualize them and feel the appreciation.</p>
      
      <h3>4. Gratitude Walks</h3>
      <p>Take a walk and notice things around you that you appreciate. Focus on your senses and the beauty around you.</p>
      
      <h3>5. Gratitude Sharing</h3>
      <p>Share what you're grateful for with family or friends. This can strengthen relationships and spread positivity.</p>
      
      <h2>Overcoming Gratitude Challenges</h2>
      <p>It's normal to struggle with gratitude, especially during difficult times. Remember:</p>
      <ul>
        <li>Start small with simple things</li>
        <li>It's okay to have bad days</li>
        <li>Focus on progress, not perfection</li>
        <li>Be patient with yourself</li>
      </ul>
    `,
    author: {
      name: 'Dr. Jennifer Kim',
      title: 'Positive Psychology Researcher',
      avatar: 'JK'
    },
    category: 'Mental Health',
    readTime: '7 min read',
    publishDate: 'February 22, 2024',
    tags: [ 'Gratitude', 'Mental Health', 'Wellness', 'Psychology', 'Happiness' ]
  }
];
